// Main charts package
import Highcharts from 'highcharts';

// Additional charts modules
import Accessibility from 'highcharts/modules/accessibility';
import ExportData from 'highcharts/modules/export-data';
import Exporting from 'highcharts/modules/exporting';
import HighContrastLight from 'highcharts/themes/high-contrast-light';
import PatternFill from 'highcharts/modules/pattern-fill';
import Timeline from 'highcharts/modules/timeline';

import axios from 'axios';

// Initialise the modules
Accessibility(Highcharts);
ExportData(Highcharts);
Exporting(Highcharts);
HighContrastLight(Highcharts);
PatternFill(Highcharts);
Timeline(Highcharts);

/**
 * Highcharts global configuration
 *
 * See: https://www.highcharts.com/docs/chart-design-and-style/themes
 * You can generally style anything you can find in here: https://api.highcharts.com/highcharts/
 */
Highcharts.setOptions({
  chart: {
      style: {
          fontFamily: '"FoundersGrotesque", sans-serif'
      }
  },
  title: {
    style: {
        color: '#060D32',
        fontWeight: '400',
        fontSize: '1.5rem'
    }
  },
  subtitle: {
    style: {
        color: '#000',
        fontSize: '0.875rem',
        lineHeight: '1.625rem'
    }
  },
  legend: {
    backgroundColor: '#fff',
    itemStyle: {
        color: '#000',
        fontSize: '0.875rem',
        lineHeight: '1.625rem'
    },
    shadow: {
      color: 'rgba(0, 0, 0, 0.5)',
      offsetX: 0,
      offsetY: 1,
      width: 3
    }
  },
  navigation: {
    menuItemStyle: {
      borderRadius: '0',
      border: '1px solid #0b1c46',
      padding: '0.3rem 1rem',
      color: '#000',
      fontSize: '0.875rem',
      lineHeight: '1.625rem'
    },
    menuItemHoverStyle: {
      borderColor: '#006cf9',
      color: '#fff',
      background: '#006cf9'
    }
  },
  xAxis: {
    labels: {
      style: {
        color: '#000',
        fontSize: '0.875rem',
        lineHeight: '1.625rem'
      }
    },
    title: {
      style: {
        color: '#000',
        fontSize: '0.875rem',
        lineHeight: '1.625rem'
      }
    },
    crosshair: {
      color: '#f8f8f8'
    },
    gridLineColor: '#b9bdc8'
  },
  yAxis: {
    labels: {
      style: {
        color: '#000',
        fontSize: '0.875rem',
        lineHeight: '1.625rem'
      }
    },
    title: {
      style: {
        color: '#000',
        fontSize: '0.875rem',
        lineHeight: '1.625rem'
      }
    },
    crosshair: {
      color: '#f8f8f8'
    },
    gridLineColor: '#b9bdc8'
  },
  plotOptions: {
    bar: {
      borderRadius: 0,
      dataLabels: {
        style: {
          color: '#000',
          fontWeight: '500'
        }
      }
    },
    column: {
      borderRadius: 0
    },
    pie: {
      dataLabels: {
        style: {
          color: '#000',
          fontSize: '0.875rem',
          lineHeight: '1.625rem',
          fontWeight: '500'
        }
      }
    }
  }
 });

// The default chart colours
const HIGHCHARTS_DEFAULT_COLOURS = [
  '#006CF9', // Colour #1
  '#060D32', // Colour #2
  '#99120d', // Colour #3 ...
  '#F56358',
  '#003d8f',
  '#5e617a',
  '#d68100',
  '#1ea4a2'
];

/**
 * Set the heights for the various chart types
 * across different breakpoints
 *
 * Note the breakpoints are calculated based on the
 * chart width, not the browser viewport width
 */
const tabletBreakpoint = 841;

// Bar charts - basic
const barChartBasicMobileHeight = '400px';
const barChartBasicDesktopHeight = '400px';

// Bar charts - grouped
const barChartGroupsMobileHeight = '400px';
const barChartGroupsDesktopHeight = '500px';

// Pie charts
const pieChartMobileHeight = '400px';
const pieChartDesktopHeight = '400px';

// Line charts
const lineChartMobileHeight = '400px';
const lineChartDesktopHeight = '400px';

// Stacked charts
const stackedChartMobileHeight = '400px';
const stackedChartDesktopHeight = '500px';

// Timeline charts
const timelineChartMobileHeight = '400px';
const timelineChartDesktopHeight = '400px';

const chartPresetHeights = {
  medium: '600px',
  maximum: '800px'
};

window.chartsEnabled = true;

/**
 * Create the markup for the charts error message
 *
 * @param {String} message
 * Short description to add to the error
 *
 * @returns String Markup to insert into the document
 */
function generateErrorMarkup(message) {
  return `<div class="feedback-label feedback-label--error">
              <span class="status-icon status-icon--error">
                  <svg class="status-icon__svg"><use xlink:href="#status-error"></use></svg>
              </span>
              <p class="body-size-sm feedback-label__text">It looks like there was a problem. ${message}</p>
          </div>`;
}

/**
 * Combine the user-defined and default colour options
 * so that there is enough variation in the colour palette.
 *
 * Optionally, generate the patterns format for accessibility.
 *
 * @param {Array} userColours
 * The list of user-defined colours
 * @param {Boolean} usePatterns
 * Toggle for generating accessible patterns
 *
 * @returns Array of chart colours as HEX Strings
 */
function generateColourPalette(userColours, usePatterns = false) {
  const combinedPalette = userColours;
  let highchartsAccessiblePatterns = [];

  // Fill any gaps in the custom colour palette with the corresponding default
  for (let k = 0, b = combinedPalette.length; k < b; ++k) {
    if (combinedPalette[k] === '') {
      combinedPalette[k] = HIGHCHARTS_DEFAULT_COLOURS[k];
    }
  }

  // Then, begin with the user-defined colours and add more as necessary
  if (combinedPalette.length < HIGHCHARTS_DEFAULT_COLOURS.length) {
    for (let i = 0, j = HIGHCHARTS_DEFAULT_COLOURS.length - combinedPalette.length; i < j; ++i) {
      combinedPalette.push(HIGHCHARTS_DEFAULT_COLOURS[i]);
    }
  }

  if (usePatterns) {
    /**
     * Generate a pattern Object for a Highcharts chart from
     * the list of combined colours and available patterns
     *
     * Merge the Highcharts pattern Object with a colour.
     *
     * @source https://www.highcharts.com/demo/accessible-pie
     */
    highchartsAccessiblePatterns = combinedPalette.map( (colour, index) => {
      return {
        pattern: Highcharts.merge(Highcharts.patterns[index], {
          color: colour
        })
      };
    });

    // Return the accessible colour Objects
    return highchartsAccessiblePatterns;
  }

  // By default, return the combined colour palette
  return combinedPalette;
}

/**
 * Create the Array needed for the category labels.
 * e.g. for the xAxis
 *
 * Expects that the first Array in the chart data is the label data
 *
 * @param {Array} chartData
 * The Array-converted version of the chart CSV data
 *
 * @returns Array Strings to use as the category labels
 */
function generateBarCategoryLabels(chartData) {
  // The first item in the Array should be blank (or otherwise ignored)
  return chartData[0].slice(1);
}

/**
 * Create the Array needed for the category labels.
 * e.g. for the xAxis
 *
 * Expects that the first Array in the chart data is the label data
 *
 * @param {Array} chartData
 * The Array-converted version of the chart CSV data
 *
 * @returns Array Strings to use as the category labels
 */
function generateLineCategoryLabels(chartData) {
  // The first three items in the Array should be blank (or otherwise ignored)
  return chartData[0].slice(3);
}

/**
 * Create the Array needed for the category labels.
 * e.g. for the xAxis
 *
 * Expects that the first Array in the chart data is the label data
 *
 * @param {Array} chartData
 * The Array-converted version of the chart CSV data
 *
 * @returns Array Strings to use as the category labels
 */
function generateComboStackedBarCategoryLabels(chartData) {
  // The first five items in the Array should be blank (or otherwise ignored)
  return chartData[0].slice(5);
}

/**
 * Create the Array in the format needed for bar charts
 * to display series data
 *
 * Expects an Array of Arrays, where;
 * Array index > 0 are the series data
 *
 * @param {Array} chartData
 * The Array-converted version of the chart CSV data
 *
 * @returns Array of Objects, one for each series of data
 */
function generateBarChartSeries(chartData) {
  const seriesDataArray = [];
  let nestedChartData = [];

  // Start at index:1 as the first Array should be chart category labels
  for (let i = 1, j = chartData.length; i < j; ++i) {
    // Capture the nested Array
    nestedChartData = chartData[i];

    // Add the series data
    seriesDataArray.push({
      name: nestedChartData.slice(0, 1)[0], // Index 0 is the series label
      data: nestedChartData.slice(1).map( value => {
         // All other indices are the data values, which need to be Numbers
        return Number(value);
      })
    });
  }

  return seriesDataArray;
}

/**
 * Create the Array in the format needed for pie charts
 * to display series data
 *
 * Expects an Array of Arrays, where;
 * Array index 0 has the series metric label
 * Array index > 0 are the series data
 *
 * @param {Array} chartData
 * The Array-converted version of the chart CSV data
 *
 * @returns Array with an Object containing the series of data
 */
function generatePieChartSeries(chartData) {
  const seriesDataArray = [];
  let nestedChartData = [];

  // Create the series data Object
  seriesDataArray.push({
    name: chartData[0].slice(0, 1)[0], // Chart metric label
    data: []
  });

  // Start at index:1 as the first Array should be chart's metric label
  for (let i = 1, j = chartData.length; i < j; ++i) {
    // Capture the nested Array
    nestedChartData = chartData[i];

    // Add the series data
    seriesDataArray[0].data.push({
      name: nestedChartData.slice(0, 1)[0], // Index 0 is the series label
      y: Number( nestedChartData.slice(1, 2)[0] ) // Index 1 is the series value, which needs to be Number
    });
  }

  return seriesDataArray;
}

/**
 * Create the Array in the format needed for line charts
 * to display series data
 *
 * Expects an Array of Arrays, where;
 * Array index > 0 are the series data
 *
 * @param {Array} chartData
 * The Array-converted version of the chart CSV data
 *
 * @returns Array of Objects, one for each series of data
 */
function generateLineChartSeries(chartData) {
  const seriesDataArray = [];
  let nestedChartData = [];

  // Start at index:1 as the first Array should be chart category labels
  for (let i = 1, j = chartData.length; i < j; ++i) {
    // Capture the nested Array
    nestedChartData = chartData[i];

    // Add the series data
    seriesDataArray.push({
      name: nestedChartData.slice(0, 1)[0], // Index 0 is the series label
      dashStyle: nestedChartData.slice(1, 2)[0], // Index 1 is the line style
      lineWidth: parseInt(nestedChartData.slice(2, 3)[0], 10), // Index 2 is the line width, which should be an Integer
      data: nestedChartData.slice(3).map( value => {
         // All other indices are the data values, which need to be Numbers or null for data gaps
        if (value === "") {
          return null
        }

        return Number(value);
      })
    });
  }

  return seriesDataArray;
}

/**
 * Create the Array in the format needed for bar charts
 * to display series data
 *
 * Expects an Array of Arrays, where;
 * Each Array is a point in the series data
 *
 * @param {Array} chartData
 * The Array-converted version of the chart CSV data
 *
 * @returns Array of Objects, one for each series of data
 */
function generateTimelineChartSeries(chartData) {
  const seriesDataArray = [];
  let nestedChartData = [];

  // Create the series data Object
  seriesDataArray.push({
    data: []
  });

  for (let i = 0, j = chartData.length; i < j; ++i) {
    // Capture the nested Array
    nestedChartData = chartData[i];

    // Add the series data
    seriesDataArray[0].data.push({
      name: nestedChartData.slice(0, 1)[0], // Index 0 is the series label
      label: nestedChartData.slice(1, 2)[0], // Index 1 is the series summary
      description: nestedChartData.slice(2, 3)[0] // Index 2 is the series description
    });
  }

  return seriesDataArray;
}

/**
 * Create the Array in the format needed for
 * combo stacked bar charts to display series data
 *
 * Expects an Array of Arrays, where;
 * Array index 1 are the column data
 * Array index 2 are the line data
 *
 * @param {Array} chartData
 * The Array-converted version of the chart CSV data
 *
 * @returns Array of Objects, one for each series of data
 */
function generateComboStackedBarChartSeries(chartData) {
  const seriesDataArray = [];

  // Note: We start at index:1 as the first Array should be chart category labels

  // Capture the bar chart data
  seriesDataArray.push({
    name: chartData[1].slice(0, 1)[0], // Index 0 is the series label
    type: 'column',
    yAxis: 1,
    data: chartData[1].slice(5).map( value => {
       // All other indices are the data values, which need to be Numbers
      return Number(value);
    }),
    tooltip: {
      valueSuffix: chartData[1].slice(1, 2)[0] // Index 1 is the unit of measurement
    }
  });

  // Capture the line chart data
  seriesDataArray.push({
    name: chartData[2].slice(0, 1)[0], // Index 0 is the series label
    type: chartData[2].slice(2, 3)[0], // Index 2 is the line type
    dashStyle: chartData[2].slice(3, 4)[0], // Index 3 is the line style
    lineWidth: parseInt(chartData[2].slice(4, 5)[0], 10), // Index 4 is the line width, which should be an Integer
    data: chartData[2].slice(5).map( value => {
       // All other indices are the data values, which need to be Numbers
      return Number(value);
    }),
    tooltip: {
      valueSuffix: chartData[2].slice(1, 2)[0] // Index 1 is the unit of measurement
    }
  });

  return seriesDataArray;
}

/**
 * Assemble the chart configuration and data then initialise the chart.
 *
 * @param {String} chartTypeId
 * The type of chart to create
 * @param {Object} chartOverrides
 * The chart configuration that is editable by the content editors
 * @param {Array} chartDataArray
 * The chart data set(s)
 * @param {HTML element} htmlElement
 * The HTML Element for the chart
 *
 * @returns void
 */
function initHighchart(chartTypeId, chartOverrides, chartDataArray, htmlElement) {
  let chartConfiguration = null;

  switch (chartTypeId) {
    /**
     * Horizontal bar chart with groups
     *
     * @source https://www.highcharts.com/demo/bar-basic
     */
    case 'bar-horizontal-groups':
      chartConfiguration = {
        chart: {
          type: 'bar',
          height: (chartOverrides.heightPreset === 'standard' ? barChartGroupsMobileHeight : chartPresetHeights[chartOverrides.heightPreset])
        },
        credits: {
          enabled: false
        },
        exporting: {
          enabled: chartOverrides.exporting.enabled,
          buttons: {
            contextButton: {
                menuItems: ['viewFullscreen']
            }
          }
        },
        title: chartOverrides.title,
        subtitle: chartOverrides.subtitle,
        xAxis: {
          categories: generateBarCategoryLabels(chartDataArray),
          title: chartOverrides.xAxis.title,
          labels: {
            enabled: chartOverrides.xAxis.labels.enabled
          }
        },
        yAxis: {
          title: chartOverrides.yAxis.title,
          labels: {
            enabled: chartOverrides.yAxis.labels.enabled
          },
          max: chartOverrides.yAxis.max,
          min: chartOverrides.yAxis.min
        },
        colors: (chartOverrides.accessibility.patterns.enabled) ? generateColourPalette(chartOverrides.colors, true) : generateColourPalette(chartOverrides.colors),
        tooltip: {
          useHTML: true,
          headerFormat: '<span class="highcharts-tooltip__title">{point.x}</span>',
          pointFormat: `<div class="highcharts-tooltip__data-row">
                          <span class="highcharts-tooltip__legend-indicator" style="background-color:{point.color}"></span>
                          <span style="color:{point.color}">{series.name}</span>: <strong>{point.y}</strong>${chartOverrides.tooltip.suffix}
                        </div>`
        },
        plotOptions: {
          bar: {
            dataLabels: {
              enabled: true
            }
          }
        },
        legend: {
          layout: chartOverrides.legend.layout,
          align: chartOverrides.legend.align,
          enabled: chartOverrides.legend.enabled,
          verticalAlign: chartOverrides.legend.verticalAlign,
          x: 0,
          y: 0,
          floating: (chartOverrides.legend.layout === 'horizontal') ? false : true,
          backgroundColor:
            Highcharts.defaultOptions.legend.backgroundColor || '#FFFFFF'
        },
        responsive: {
          rules: [{
            condition: {
              minWidth: tabletBreakpoint
            },
            chartOptions: {
              chart: {
                height: (chartOverrides.heightPreset === 'standard' ? barChartGroupsDesktopHeight : chartPresetHeights[chartOverrides.heightPreset])
              }
            }
          }]
        },
        series: generateBarChartSeries(chartDataArray)
      };
      break;

    /**
     * Vertical bar chart with groups
     *
     * @source https://www.highcharts.com/demo/column-basic
     */
    case 'bar-vertical-groups':
      chartConfiguration = {
        chart: {
          type: 'column',
          height: (chartOverrides.heightPreset === 'standard' ? barChartGroupsMobileHeight : chartPresetHeights[chartOverrides.heightPreset])
        },
        credits: {
          enabled: false
        },
        exporting: {
          enabled: chartOverrides.exporting.enabled,
          buttons: {
            contextButton: {
                menuItems: ['viewFullscreen']
            }
          }
        },
        title: chartOverrides.title,
        subtitle: chartOverrides.subtitle,
        xAxis: {
          categories: generateBarCategoryLabels(chartDataArray),
          title: chartOverrides.xAxis.title,
          labels: {
            enabled: chartOverrides.xAxis.labels.enabled
          },
          crosshair: {
            color: '#f8f8f8'
          }
        },
        yAxis: {
          title: chartOverrides.yAxis.title,
          labels: {
            enabled: chartOverrides.yAxis.labels.enabled
          },
          max: chartOverrides.yAxis.max,
          min: chartOverrides.yAxis.min
        },
        colors: (chartOverrides.accessibility.patterns.enabled) ? generateColourPalette(chartOverrides.colors, true) : generateColourPalette(chartOverrides.colors),
        tooltip: {
          headerFormat: '<span class="highcharts-tooltip__title">{point.key}</span><table>',
          pointFormat: `<tr>
                          <td style="color:{series.color};">{series.name}: </td>
                          <td style="padding:0"><strong>{point.y:.1f}${chartOverrides.tooltip.suffix}</strong></td>
                        </tr>`,
          footerFormat: '</table>',
          shared: true,
          useHTML: true
        },
        plotOptions: {
          column: {
            pointPadding: 0.2,
            borderWidth: 0
          }
        },
        legend: {
          layout: chartOverrides.legend.layout,
          align: chartOverrides.legend.align,
          enabled: chartOverrides.legend.enabled,
          verticalAlign: chartOverrides.legend.verticalAlign,
          x: 0,
          y: 0,
          floating: (chartOverrides.legend.layout === 'horizontal') ? false : true,
          backgroundColor:
            Highcharts.defaultOptions.legend.backgroundColor || '#FFFFFF'
        },
        responsive: {
          rules: [{
            condition: {
              minWidth: tabletBreakpoint
            },
            chartOptions: {
              chart: {
                height: (chartOverrides.heightPreset === 'standard' ? barChartGroupsDesktopHeight : chartPresetHeights[chartOverrides.heightPreset])
              }
            }
          }]
        },
        series: generateBarChartSeries(chartDataArray)
      };
      break;

    /**
     * Simple vertical bar chart
     *
     * @source https://www.highcharts.com/demo/column-basic
     */
    case 'bar-vertical':
      chartConfiguration = {
        chart: {
          type: 'column',
          height: (chartOverrides.heightPreset === 'standard' ? barChartBasicMobileHeight : chartPresetHeights[chartOverrides.heightPreset])
        },
        credits: {
          enabled: false
        },
        exporting: {
          enabled: chartOverrides.exporting.enabled,
          buttons: {
            contextButton: {
                menuItems: ['viewFullscreen']
            }
          }
        },
        title: chartOverrides.title,
        subtitle: chartOverrides.subtitle,
        xAxis: {
          categories: generateBarCategoryLabels(chartDataArray),
          title: chartOverrides.xAxis.title,
          labels: {
            enabled: chartOverrides.xAxis.labels.enabled
          }
        },
        yAxis: {
          title: chartOverrides.yAxis.title,
          labels: {
            enabled: chartOverrides.yAxis.labels.enabled
          },
          max: chartOverrides.yAxis.max,
          min: chartOverrides.yAxis.min
        },
        colors: (chartOverrides.accessibility.patterns.enabled) ? generateColourPalette(chartOverrides.colors, true) : generateColourPalette(chartOverrides.colors),
        tooltip: {
          useHTML: true,
          headerFormat: '<span class="highcharts-tooltip__title">{series.name}</span>',
          pointFormat: `<div class="highcharts-tooltip__data-row">
                          <span class="highcharts-tooltip__legend-indicator" style="background-color:{point.color}"></span>
                          <strong>{point.y:.1f}</strong>${chartOverrides.tooltip.suffix}
                        </div>`
        },
        plotOptions: {
          series: {
            borderWidth: 0,
            dataLabels: {
              enabled: false
            }
          }
        },
        legend: {
          layout: chartOverrides.legend.layout,
          align: chartOverrides.legend.align,
          enabled: chartOverrides.legend.enabled,
          verticalAlign: chartOverrides.legend.verticalAlign,
          x: 0,
          y: 0,
          floating: (chartOverrides.legend.layout === 'horizontal') ? false : true,
          backgroundColor:
            Highcharts.defaultOptions.legend.backgroundColor || '#FFFFFF'
        },
        responsive: {
          rules: [{
            condition: {
              minWidth: tabletBreakpoint
            },
            chartOptions: {
              chart: {
                height: (chartOverrides.heightPreset === 'standard' ? barChartBasicDesktopHeight : chartPresetHeights[chartOverrides.heightPreset])
              }
            }
          }]
        },
        series: generateBarChartSeries(chartDataArray)
      };
      break;

    /**
     * Pie chart
     *
     * @source https://www.highcharts.com/demo/accessible-pie
     */
    case 'pie-accessible':
      chartConfiguration = {
        chart: {
          type: 'pie',
          height: (chartOverrides.heightPreset === 'standard' ? pieChartMobileHeight : chartPresetHeights[chartOverrides.heightPreset])
        },
        credits: {
          enabled: false
        },
        exporting: {
          enabled: chartOverrides.exporting.enabled,
          buttons: {
            contextButton: {
                menuItems: ['viewFullscreen']
            }
          }
        },
        title: chartOverrides.title,
        subtitle: chartOverrides.subtitle,
        colors: (chartOverrides.accessibility.patterns.enabled) ? generateColourPalette(chartOverrides.colors, true) : generateColourPalette(chartOverrides.colors),
        tooltip: {
          borderColor: '#0b1c46',
          useHTML: true,
          headerFormat: '<span class="highcharts-tooltip__title">{point.point.name}</span>',
          pointFormat: `<div class="highcharts-tooltip__data-row">
                          <span style="color:{point.color}">{series.name}</span>: <strong>{point.y}</strong>${chartOverrides.tooltip.suffix}
                        </div>`
        },
        plotOptions: {
          series: {
            dataLabels: {
              enabled: chartOverrides.plotOptions.series.dataLabels.enabled,
              connectorColor: '#0b1c46',
              format: `{point.name}: {point.percentage:.1f}${chartOverrides.tooltip.suffix}`
            },
            borderWidth: 3,
            showInLegend: chartOverrides.legend.enabled
          }
        },
        legend: {
          layout: chartOverrides.legend.layout,
          align: chartOverrides.legend.align,
          verticalAlign: chartOverrides.legend.verticalAlign,
          x: 0,
          y: 0,
          floating: false,
          backgroundColor:
            Highcharts.defaultOptions.legend.backgroundColor || '#FFFFFF'
        },
        series: generatePieChartSeries(chartDataArray),
        responsive: {
          rules: [{
            condition: {
              minWidth: tabletBreakpoint
            },
            chartOptions: {
              chart: {
                height: (chartOverrides.heightPreset === 'standard' ? pieChartDesktopHeight : chartPresetHeights[chartOverrides.heightPreset])
              }
            }
          }]
        }
      };
      break;

    /**
     * Line chart
     *
     * @source https://www.highcharts.com/demo/line-basic
     */
    case 'line':
      chartConfiguration = {
        chart: {
          type: 'line',
          height: (chartOverrides.heightPreset === 'standard' ? lineChartMobileHeight : chartPresetHeights[chartOverrides.heightPreset])
        },
        credits: {
          enabled: false
        },
        exporting: {
          enabled: chartOverrides.exporting.enabled,
          buttons: {
            contextButton: {
                menuItems: ['viewFullscreen']
            }
          }
        },
        title: chartOverrides.title,
        subtitle: chartOverrides.subtitle,
        xAxis: {
          categories: generateLineCategoryLabels(chartDataArray),
          title: chartOverrides.xAxis.title,
          labels: {
            enabled: chartOverrides.xAxis.labels.enabled
          }
        },
        yAxis: {
          title: chartOverrides.yAxis.title,
          labels: {
            enabled: chartOverrides.yAxis.labels.enabled
          },
          max: chartOverrides.yAxis.max,
          min: chartOverrides.yAxis.min
        },
        colors: (chartOverrides.accessibility.patterns.enabled) ? generateColourPalette(chartOverrides.colors, true) : generateColourPalette(chartOverrides.colors),
        tooltip: {
          useHTML: true,
          headerFormat: '<span class="highcharts-tooltip__title">{point.x}</span>',
          pointFormat: `<div class="highcharts-tooltip__data-row">
                          <span class="highcharts-tooltip__legend-indicator" style="background-color:{point.color}"></span>
                          <span style="color:{point.color}">{series.name}</span>: <strong>{point.y}</strong>${chartOverrides.tooltip.suffix}
                        </div>`
        },
        plotOptions: {
          series: {
            label: {
              connectorAllowed: false
            }
          }
        },
        legend: {
          layout: chartOverrides.legend.layout,
          align: chartOverrides.legend.align,
          enabled: chartOverrides.legend.enabled,
          verticalAlign: chartOverrides.legend.verticalAlign,
          x: 0,
          y: 0,
          backgroundColor:
            Highcharts.defaultOptions.legend.backgroundColor || '#FFFFFF'
        },
        series: generateLineChartSeries(chartDataArray),
        responsive: {
          rules: [{
            condition: {
              minWidth: tabletBreakpoint
            },
            chartOptions: {
              chart: {
                height: (chartOverrides.heightPreset === 'standard' ? lineChartDesktopHeight : chartPresetHeights[chartOverrides.heightPreset])
              }
            }
          }]
        }
      };
      break;

    /**
     * Stacked vertical bar chart
     *
     * @source https://www.highcharts.com/demo/column-stacked
     */
    case 'bar-vertical-stacked':
      chartConfiguration = {
        chart: {
          type: 'column',
          height: (chartOverrides.heightPreset === 'standard' ? stackedChartMobileHeight : chartPresetHeights[chartOverrides.heightPreset])
        },
        credits: {
          enabled: false
        },
        exporting: {
          enabled: chartOverrides.exporting.enabled,
          buttons: {
            contextButton: {
                menuItems: ['viewFullscreen']
            }
          }
        },
        title: chartOverrides.title,
        subtitle: chartOverrides.subtitle,
        xAxis: {
          categories: generateBarCategoryLabels(chartDataArray),
          title: chartOverrides.xAxis.title,
          labels: {
            enabled: chartOverrides.xAxis.labels.enabled
          }
        },
        yAxis: {
          title: chartOverrides.yAxis.title,
          labels: {
            enabled: chartOverrides.yAxis.labels.enabled
          },
          stackLabels: {
            enabled: chartOverrides.yAxis.stackLabels.enabled,
            style: {
              fontWeight: '500',
              color: (
                Highcharts.defaultOptions.title.style &&
                Highcharts.defaultOptions.title.style.color
              ) || '#0b1c46'
            }
          },
          max: chartOverrides.yAxis.max,
          min: chartOverrides.yAxis.min
        },
        colors: (chartOverrides.accessibility.patterns.enabled) ? generateColourPalette(chartOverrides.colors, true) : generateColourPalette(chartOverrides.colors),
        tooltip: {
          useHTML: true,
          headerFormat: '<span class="highcharts-tooltip__title">{point.x}</span>',
          pointFormat: `<div class="highcharts-tooltip__data-row">
                          <span style="color:{point.color}">{series.name}</span>: <strong>{point.y}</strong>
                          <div class="highcharts-tooltip__data-row">Total: {point.stackTotal}${chartOverrides.tooltip.suffix}</div>
                        </div>`
        },
        plotOptions: {
          column: {
            stacking: chartOverrides.column.stacking,
            dataLabels: {
              enabled: chartOverrides.column.dataLabels.enabled
            }
          }
        },
        legend: {
          layout: chartOverrides.legend.layout,
          align: chartOverrides.legend.align,
          enabled: chartOverrides.legend.enabled,
          verticalAlign: chartOverrides.legend.verticalAlign,
          x: 0,
          y: 0,
          backgroundColor:
            Highcharts.defaultOptions.legend.backgroundColor || '#FFFFFF'
        },
        responsive: {
          rules: [{
            condition: {
              minWidth: tabletBreakpoint
            },
            chartOptions: {
              chart: {
                height: (chartOverrides.heightPreset === 'standard' ? stackedChartDesktopHeight : chartPresetHeights[chartOverrides.heightPreset])
              }
            }
          }]
        },
        series: generateBarChartSeries(chartDataArray)
      };
      break;

    /**
     * Timeline chart
     *
     * @source https://www.highcharts.com/demo/timeline
     */
    case 'timeline':
      chartConfiguration = {
        chart: {
          type: 'timeline',
          height: (chartOverrides.heightPreset === 'standard' ? timelineChartMobileHeight : chartPresetHeights[chartOverrides.heightPreset])
        },
        credits: {
          enabled: false
        },
        exporting: {
          enabled: chartOverrides.exporting.enabled,
          buttons: {
            contextButton: {
                menuItems: ['viewFullscreen']
            }
          }
        },
        title: chartOverrides.title,
        subtitle: chartOverrides.subtitle,
        accessibility: {
          screenReaderSection: {
            beforeChartFormat: '<h5>{chartTitle}</h5>' +
              '<div>{typeDescription}</div>' +
              '<div>{chartSubtitle}</div>' +
              '<div>{chartLongdesc}</div>' +
              '<div>{viewTableButton}</div>'
          },
          point: {
            valueDescriptionFormat: '{index}. {point.label}. {point.description}.'
          }
        },
        xAxis: {
          visible: false
        },
        yAxis: {
          visible: false
        },
        colors: (chartOverrides.accessibility.patterns.enabled) ? generateColourPalette(chartOverrides.colors, true) : generateColourPalette(chartOverrides.colors),
        tooltip: {
          useHTML: true,
          headerFormat: '<span class="highcharts-tooltip__title">{point.key}</span>',
          pointFormat: `<div class="highcharts-tooltip__data-row">
                          <span class="highcharts-tooltip__legend-indicator" style="background-color:{point.color}"></span>
                          <span>{point.description}</span>
                        </div>`,
          style: {
            width: '300px'
          }
        },
        plotOptions: {
          series: {
            showInLegend: chartOverrides.legend.enabled
          }
        },
        legend: {
          layout: chartOverrides.legend.layout,
          align: chartOverrides.legend.align,
          enabled: chartOverrides.legend.enabled,
          verticalAlign: chartOverrides.legend.verticalAlign,
          x: 0,
          y: 0,
          backgroundColor:
            Highcharts.defaultOptions.legend.backgroundColor || '#FFFFFF'
        },
        responsive: {
          rules: [{
            condition: {
              minWidth: tabletBreakpoint
            },
            chartOptions: {
              chart: {
                height: (chartOverrides.heightPreset === 'standard' ? timelineChartDesktopHeight : chartPresetHeights[chartOverrides.heightPreset])
              }
            }
          }]
        },
        series: generateTimelineChartSeries(chartDataArray)
      };
      break;

    /**
     * Simple horizontal bar chart
     *
     * @source https://www.highcharts.com/demo/bar-basic
     */
    case 'bar-horizontal':
      chartConfiguration = {
        chart: {
          type: 'bar',
          height: (chartOverrides.heightPreset === 'standard' ? barChartBasicMobileHeight : chartPresetHeights[chartOverrides.heightPreset])
        },
        credits: {
          enabled: false
        },
        exporting: {
          enabled: chartOverrides.exporting.enabled,
          buttons: {
            contextButton: {
                menuItems: ['viewFullscreen']
            }
          }
        },
        title: chartOverrides.title,
        subtitle: chartOverrides.subtitle,
        xAxis: {
          categories: generateBarCategoryLabels(chartDataArray),
          title: chartOverrides.xAxis.title,
          labels: {
            enabled: chartOverrides.xAxis.labels.enabled
          },
          crosshair: {
            color: '#f8f8f8'
          }
        },
         yAxis: {
          title: chartOverrides.yAxis.title,
          labels: {
            enabled: chartOverrides.yAxis.labels.enabled
          },
          max: chartOverrides.yAxis.max,
          min: chartOverrides.yAxis.min
        },
        colors: (chartOverrides.accessibility.patterns.enabled) ? generateColourPalette(chartOverrides.colors, true) : generateColourPalette(chartOverrides.colors),
        tooltip: {
          useHTML: true,
          headerFormat: '<span class="highcharts-tooltip__title">{point.key}</span>',
          pointFormat: `<div class="highcharts-tooltip__data-row">
              <span style="color:{point.color}">{series.name}</span>: <strong>{point.y:.1f}</strong>${chartOverrides.tooltip.suffix}
          </div>`
        },
        plotOptions: {
          column: {
            pointPadding: 0.2,
            borderWidth: 0
          }
        },
        legend: {
          layout: chartOverrides.legend.layout,
          align: chartOverrides.legend.align,
          enabled: chartOverrides.legend.enabled,
          verticalAlign: chartOverrides.legend.verticalAlign,
          x: 0,
          y: 0,
          floating: (chartOverrides.legend.layout === 'horizontal') ? false : true,
          backgroundColor:
            Highcharts.defaultOptions.legend.backgroundColor || '#FFFFFF'
        },
        responsive: {
          rules: [{
            condition: {
              minWidth: tabletBreakpoint
            },
            chartOptions: {
              chart: {
                height: (chartOverrides.heightPreset === 'standard' ? barChartBasicDesktopHeight : chartPresetHeights[chartOverrides.heightPreset])
              }
            }
          }]
        },
        series: generateBarChartSeries(chartDataArray)
      };
      break;

    /**
     * Combo stacked bar chart and line chart
     *
     * @source https://www.highcharts.com/demo/combo-dual-axes
     */
    case 'bar-combo-stacked':
      chartConfiguration = {
        chart: {
          zoomType: 'xy',
          height: (chartOverrides.heightPreset === 'standard' ? stackedChartMobileHeight : chartPresetHeights[chartOverrides.heightPreset])
        },
        credits: {
          enabled: false
        },
        exporting: {
          enabled: chartOverrides.exporting.enabled,
          buttons: {
            contextButton: {
                menuItems: ['viewFullscreen']
            }
          }
        },
        title: chartOverrides.title,
        subtitle: chartOverrides.subtitle,
        xAxis: {
          categories: generateComboStackedBarCategoryLabels(chartDataArray),
          title: chartOverrides.xAxis.title,
          labels: {
            enabled: chartOverrides.xAxis.labels.enabled
          },
          crosshair: {
            color: '#f8f8f8'
          }
        },
        yAxis: [
          { // Primary yAxis
            labels: {
              format: `{value}${chartDataArray[2][1]}`,
              enabled: chartOverrides.yAxis[0].labels.enabled
            },
            title: chartOverrides.yAxis[0].title,
            max: chartOverrides.yAxis[0].max,
            min: chartOverrides.yAxis[0].min
          },
          { // Secondary yAxis
            labels: {
              format: `{value}${chartDataArray[1][1]}`,
              enabled: chartOverrides.yAxis[1].labels.enabled
            },
            title: chartOverrides.yAxis[1].title,
            opposite: true
          }
        ],
        colors: (chartOverrides.accessibility.patterns.enabled) ? generateColourPalette(chartOverrides.colors, true) : generateColourPalette(chartOverrides.colors),
        tooltip: {
          shared: true,
          useHTML: true,
          headerFormat: '<span class="highcharts-tooltip__title">{point.x}</span>',
          pointFormat: `<div class="highcharts-tooltip__data-row">
                          <span class="highcharts-tooltip__legend-indicator" style="background-color:{point.color}"></span>
                          <span style="color:{point.color}">{series.name}</span>: <strong>{point.y}</strong>
                        </div>`
        },
        legend: {
          layout: chartOverrides.legend.layout,
          align: chartOverrides.legend.align,
          enabled: chartOverrides.legend.enabled,
          verticalAlign: chartOverrides.legend.verticalAlign,
          x: 0,
          y: 0,
          backgroundColor:
            Highcharts.defaultOptions.legend.backgroundColor || '#FFFFFF'
        },
        responsive: {
          rules: [{
            condition: {
              minWidth: tabletBreakpoint
            },
            chartOptions: {
              chart: {
                height: (chartOverrides.heightPreset === 'standard' ? stackedChartDesktopHeight : chartPresetHeights[chartOverrides.heightPreset])
              }
            }
          }]
        },
        series: generateComboStackedBarChartSeries(chartDataArray)
      };
      break;

    /**
     * Stacked horizontal bar chart
     *
     * @source https://www.highcharts.com/demo/bar-stacked
     */
    case 'bar-horizontal-stacked':
      chartConfiguration = {
        chart: {
          type: 'bar',
          height: (chartOverrides.heightPreset === 'standard' ? stackedChartMobileHeight : chartPresetHeights[chartOverrides.heightPreset])
        },
        credits: {
          enabled: false
        },
        exporting: {
          enabled: chartOverrides.exporting.enabled,
          buttons: {
            contextButton: {
                menuItems: ['viewFullscreen']
            }
          }
        },
        title: chartOverrides.title,
        subtitle: chartOverrides.subtitle,
        xAxis: {
          categories: generateBarCategoryLabels(chartDataArray),
          title: chartOverrides.xAxis.title,
          labels: {
            enabled: chartOverrides.xAxis.labels.enabled
          }
        },
        yAxis: {
          // min: 0, // Remove this to have negative values in the chart
          title: chartOverrides.yAxis.title,
          labels: {
            enabled: chartOverrides.yAxis.labels.enabled
          },
          stackLabels: {
            enabled: chartOverrides.yAxis.stackLabels.enabled,
            style: {
              fontWeight: '500',
              color: (
                Highcharts.defaultOptions.title.style &&
                Highcharts.defaultOptions.title.style.color
              ) || '#0b1c46'
            }
          },
          max: chartOverrides.yAxis.max,
          min: chartOverrides.yAxis.min
        },
        colors: (chartOverrides.accessibility.patterns.enabled) ? generateColourPalette(chartOverrides.colors, true) : generateColourPalette(chartOverrides.colors),
        tooltip: {
          useHTML: true,
          headerFormat: '<span class="highcharts-tooltip__title">{point.x}</span>',
          pointFormat: `<div class="highcharts-tooltip__data-row">
                          <span style="color:{point.color}">{series.name}</span>: <strong>{point.y}</strong>${chartOverrides.tooltip.suffix}
                        </div>`
        },
        plotOptions: {
          series: {
            stacking: chartOverrides.column.stacking,
            dataLabels: {
              enabled: chartOverrides.column.dataLabels.enabled
            }
          }
        },
        legend: {
          layout: chartOverrides.legend.layout,
          align: chartOverrides.legend.align,
          enabled: chartOverrides.legend.enabled,
          verticalAlign: chartOverrides.legend.verticalAlign,
          x: 0,
          y: 0,
          backgroundColor:
            Highcharts.defaultOptions.legend.backgroundColor || '#FFFFFF',
          reversed: true
        },
        responsive: {
          rules: [{
            condition: {
              minWidth: tabletBreakpoint
            },
            chartOptions: {
              chart: {
                height: (chartOverrides.heightPreset === 'standard' ? stackedChartDesktopHeight : chartPresetHeights[chartOverrides.heightPreset])
              }
            }
          }]
        },
        series: generateBarChartSeries(chartDataArray)
      };
      break;

    default:
      break;
  }

  if (chartConfiguration !== null) {
    // Initialise the chart
    Highcharts.chart(htmlElement, chartConfiguration);
  } else {
    // Unknown chart type, show error
    htmlElement.insertAdjacentHTML('beforebegin', generateErrorMarkup(`Unable to determine the chart type for this chart. The chart type provided was: "${chartTypeId}"`));
  }
}

/**
 * Locate any/all chart elements on the page and intialise them.
 */
const highchartElements = document.getElementsByClassName('js-highcharts-element');

for (let item of highchartElements) {
  const chartTypeId = item.getAttribute('data-chart-type');
  const chartDataEndpoint = item.getAttribute('data-chart-data-endpoint');
  const chartOverrides = JSON.parse( decodeURIComponent(item.getAttribute('data-chart-config-json')) );

  // Check if the chart data endpoint has been supplied
  if (!chartDataEndpoint) {
    // Chart config object not found, show error
    item.insertAdjacentHTML('beforebegin', generateErrorMarkup("No chart data has been provided for this chart."));

    // Continue to the next iteration
    continue;
  }

  // Check if the chart configuration has been supplied
  if (chartOverrides === null) {
    // Chart config object not found, show error
    item.insertAdjacentHTML('beforebegin', generateErrorMarkup("Unable to find configuration data for this chart."));

    // Continue to the next iteration
    continue;
  }

  // Request the chart CSV data
  axios.get(chartDataEndpoint)
    .then(response => {

      if (response.data.csvData.length === 0) {
        item.insertAdjacentHTML('beforebegin', generateErrorMarkup("No chart data has been provided for this chart."));
        return;
      }

      initHighchart(chartTypeId, chartOverrides, response.data.csvData, item);
    })
    .catch(error => {
      item.insertAdjacentHTML('beforebegin', generateErrorMarkup(`Unable to display this chart. Error message: ${error}`));
    });
}
